import { useEffect, useState } from "react";
import { Play, X } from "lucide-react";
import { gifPublicUrl } from "./workoutDetail";
import { youTubeEmbedUrl } from "@/lib/youtube";

/**
 * Visualizador de mídia do exercício. Prioridade: vídeo (YouTube embed ou
 * HTML5 controls) > gif (img lazy) > 1ª imagem de image_urls. Sem mídia →
 * placeholder com play. Portado de `frontend/components/student-portal/MediaViewer.tsx`.
 *
 * `video_url` aceita DOIS tipos: link do YouTube (o trainer cola; toca via
 * iframe) ou arquivo de vídeo do storage (legado do upload antigo; toca em
 * <video>). Ver `@/lib/youtube`.
 */
function MediaViewer({
  videoUrl,
  gifPath,
  imageUrls,
  alt = "Mídia do exercício",
}: {
  videoUrl?: string | null;
  gifPath?: string | null;
  imageUrls?: string[];
  alt?: string;
}) {
  const [failed, setFailed] = useState(false);
  const wrapper =
    "relative w-full overflow-hidden rounded-xl border aspect-video bg-elevated border-border-subtle";

  if (videoUrl && !failed) {
    // Link do YouTube não toca em <video> — precisa do iframe de embed.
    // Arquivo de vídeo (upload legado) segue no <video> abaixo.
    const embedUrl = youTubeEmbedUrl(videoUrl);
    if (embedUrl) {
      return (
        <div className={wrapper}>
          <iframe
            src={embedUrl}
            title={alt}
            className="h-full w-full"
            // Sem `autoplay`: o aluno decide quando tocar (e iOS bloqueia mesmo).
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>
      );
    }
    return (
      <div className={wrapper}>
        <video
          src={videoUrl}
          controls
          playsInline
          preload="metadata"
          className="h-full w-full object-cover"
          aria-label={alt}
          onError={() => setFailed(true)}
        />
      </div>
    );
  }

  if (gifPath && !failed) {
    const gifSrc = gifPublicUrl(gifPath);
    if (gifSrc) {
      return (
        <div className={wrapper}>
          <img
            src={gifSrc}
            alt={alt}
            loading="lazy"
            className="h-full w-full object-cover"
            onError={() => setFailed(true)}
          />
        </div>
      );
    }
  }

  if (imageUrls && imageUrls.length > 0 && !failed) {
    return (
      <div className={wrapper}>
        <img
          src={imageUrls[0]}
          alt={alt}
          loading="lazy"
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      </div>
    );
  }

  return (
    <div className={wrapper} aria-label="Sem mídia disponível">
      <div className="absolute inset-0 flex items-center justify-center">
        <Play size={36} className="text-faint opacity-60" aria-hidden />
      </div>
    </div>
  );
}

interface MediaModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  videoUrl?: string | null;
  gifPath?: string | null;
  imageUrls?: string[];
}

/**
 * Modal grande de "Ver demonstração". Lock de scroll + fecha no Escape e no
 * backdrop. Portado de `MediaPreviewModal.tsx`.
 */
export function MediaModal({
  open,
  onClose,
  title,
  videoUrl,
  gifPath,
  imageUrls,
}: MediaModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Demonstração: ${title}`}
      className="animate-fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-2xl rounded-2xl border border-border-subtle bg-surface/95 p-3 backdrop-blur-xl sm:p-4">
        <header className="mb-3 flex items-center justify-between gap-3 px-1">
          <h2 className="truncate font-display text-base text-ink sm:text-lg">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-faint transition-colors hover:bg-surface-hover hover:text-ink"
            aria-label="Fechar"
          >
            <X size={16} aria-hidden />
          </button>
        </header>
        <MediaViewer videoUrl={videoUrl} gifPath={gifPath} imageUrls={imageUrls} alt={title} />
      </div>
    </div>
  );
}
